import React from 'react'
import {useTranslation} from 'react-i18next'

import {
  ModalContent,
  ModalBody,
  AvailabilityDetailsCard,
} from './StaffAssignmentPage.styled'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {Button} from '~/components/general/button/Button'
import {SideModal} from '~/components/general/modal/SideModal'

export type TimeRange = {startTime: string; endTime: string}

type Props = {
  isVisible: boolean
  isoDate: string
  ranges: TimeRange[]
  rangeErrors: Array<{start?: string; end?: string}>
  isSaving: boolean
  hasInvalidRanges: boolean
  availabilityText: {
    timeOff: string
    partner: string
    staff: string
    assigned: string
  }
  onClose: () => void
  onSave: () => void
  onChangeStart: (index: number, value: string) => void
  onChangeEnd: (index: number, value: string) => void
  onBlurStart: (index: number) => void
  onBlurEnd: (index: number) => void
  onRemoveRange: (index: number) => void
  onAddRange: () => void
}

export const StaffAssignmentDayRangesModal: React.FC<Props> = ({
  isVisible,
  isoDate,
  ranges,
  rangeErrors,
  isSaving,
  hasInvalidRanges,
  availabilityText,
  onClose,
  onSave,
  onChangeStart,
  onChangeEnd,
  onBlurStart,
  onBlurEnd,
  onRemoveRange,
  onAddRange,
}) => {
  const {t} = useTranslation<string>()

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Typography size="lg" weight="medium">
          {t('Edit day ranges')}
        </Typography>
      }>
      <ModalContent direction="column" spacing={2}>
        <ModalBody direction="column" spacing={2}>
          <Typography>{isoDate}</Typography>
          <Container direction="column" spacing={1}>
            {(ranges || []).map((r, i) => (
              <Container key={i} flex spacing={2} align="center">
                <Input
                  size="normal"
                  type="time"
                  value={r.startTime}
                  onChange={(e) => onChangeStart(i, e.target.value)}
                  onBlur={() => onBlurStart(i)}
                  required
                  errorMessage={rangeErrors[i]?.start}
                />
                <Typography>{t('to')}</Typography>
                <Input
                  size="normal"
                  type="time"
                  value={r.endTime}
                  onChange={(e) => onChangeEnd(i, e.target.value)}
                  onBlur={() => onBlurEnd(i)}
                  required
                  errorMessage={rangeErrors[i]?.end}
                />
                <Button size="sm" onClick={() => onRemoveRange(i)}>
                  {t('Remove')}
                </Button>
              </Container>
            ))}
            <Button size="sm" onClick={onAddRange}>
              {t('Add range')}
            </Button>
          </Container>

          <Container flex justify="flex-end" spacing={2}>
            <Button onClick={onClose}>{t('Cancel')}</Button>
            <Button
              color="primary"
              onClick={onSave}
              disabled={isSaving || hasInvalidRanges}>
              {t('Save')}
            </Button>
          </Container>
        </ModalBody>

        <AvailabilityDetailsCard direction="column" spacing={1}>
          <Typography weight="medium" size="sm">
            {t('Availability details')}
          </Typography>
          <Typography size="sm">{`${t('Time Off of Staff')}: ${
            availabilityText.timeOff
          }`}</Typography>
          <Typography size="sm">{`${t('Partner working hours')}: ${
            availabilityText.partner
          }`}</Typography>
          <Typography size="sm">{`${t('Staff working hours')}: ${
            availabilityText.staff
          }`}</Typography>
          <Typography size="sm">{`${t('Already assigned slots')}: ${
            availabilityText.assigned
          }`}</Typography>
        </AvailabilityDetailsCard>
      </ModalContent>
    </SideModal>
  )
}

export default StaffAssignmentDayRangesModal
